import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { NgbDate, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns';
import { takeUntil } from 'rxjs/operators';

import { QuickRange, QuickRangeKey } from '../../../modules/children-portfolio/constants/types';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeFilterComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateRangeFilterComponent extends BaseComponent implements OnInit {
  @Input() localStorageKey: string | null = null;
  @Input() filterForm: FormGroup;
  @Input() formControlDateQuickRangeKeyName: string | null = null;
  @Input() formControlDateStartName: string;
  @Input() formControlDateEndName: string;
  /** A range to start with (e.g. carried over in the URL); it wins over the stored one and is stored in its place. */
  @Input() initialRange: { dateStart: NgbDateStruct | null; dateEnd: NgbDateStruct | null } | null = null;

  @ViewChild('dateRangePicker', { static: false }) datePicker!: NgbInputDatepicker;

  private readonly translocoService = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected hoveredDate: NgbDate | null = null;

  protected quickRanges = new Map<QuickRangeKey, QuickRange>([
    [
      'thisWeek',
      {
        label: this.translocoService.translate('dateRangeFilter/thisWeek'),
        range: () => {
          const now = new Date();
          return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
        },
      },
    ],
    [
      'lastWeek',
      {
        label: this.translocoService.translate('dateRangeFilter/lastWeek'),
        range: () => {
          const lastWeek = subWeeks(new Date(), 1);
          return { start: startOfWeek(lastWeek, { weekStartsOn: 1 }), end: endOfWeek(lastWeek, { weekStartsOn: 1 }) };
        },
      },
    ],
    [
      'lastTwoWeeks',
      {
        label: this.translocoService.translate('dateRangeFilter/lastTwoWeeks'),
        range: () => {
          const today = new Date();
          const fourteenDaysAgo = subDays(today, 14);
          return { start: startOfDay(fourteenDaysAgo), end: endOfDay(today) };
        },
      },
    ],
    [
      'lastMonth',
      {
        label: this.translocoService.translate('dateRangeFilter/lastMonth'),
        range: () => {
          const lastMonth = subMonths(new Date(), 1);
          return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
        },
      },
    ],
    [
      'lastThirtyDays',
      {
        label: this.translocoService.translate('dateRangeFilter/lastThirtyDays'),
        range: () => {
          const today = new Date();
          const thirtyDaysAgo = subDays(today, 30);
          return { start: startOfDay(thirtyDaysAgo), end: endOfDay(today) };
        },
      },
    ],
    [
      'thisSchoolYear',
      {
        label: this.translocoService.translate('dateRangeFilter/thisSchoolYear'),
        range: () => {
          const today = new Date();
          const year = today.getFullYear();
          const month = today.getMonth();
          const startYear = month >= 8 ? year : year - 1;

          const start = new Date(startYear, 8, 1);
          const end = new Date(startYear + 1, 7, 31, 23, 59, 59, 999);

          return { start, end };
        },
      },
    ],
  ]);

  ngOnInit(): void {
    // načti uložený stav z localStorage
    if (this.localStorageKey) {
      const localStorageKey = this.localStorageKey;
      const localStorageValue = localStorage.getItem(localStorageKey);
      if (localStorageValue) {
        this.filterForm.patchValue(JSON.parse(localStorageValue));
      }

      this.filterForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
        localStorage.setItem(localStorageKey, JSON.stringify(value));
        this.cdr.markForCheck();
      });
    } else {
      this.filterForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }

    if (this.initialRange) {
      this.filterForm.patchValue({
        ...(this.formControlDateQuickRangeKeyName ? { [this.formControlDateQuickRangeKeyName]: null } : {}),
        [this.formControlDateStartName]: this.initialRange.dateStart,
        [this.formControlDateEndName]: this.initialRange.dateEnd,
      });
    }
  }

  private setRange(start: Date, end: Date, key: QuickRangeKey | null = null) {
    const startNgb = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
    const endNgb = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());

    this.filterForm.get(this.formControlDateStartName)?.setValue(startNgb);
    this.filterForm.get(this.formControlDateEndName)?.setValue(endNgb);

    if (this.formControlDateQuickRangeKeyName) {
      this.filterForm.get(this.formControlDateQuickRangeKeyName)?.setValue(key);
    }

    if (this.datePicker) {
      this.datePicker.close();
    }
  }

  applyQuickRange(key: QuickRangeKey) {
    const quickRange = this.quickRanges.get(key);
    if (!quickRange) {
      return;
    }
    const { start, end } = quickRange.range();
    this.setRange(start, end, key);
  }

  onDateSelection(date: NgbDate) {
    const dateStart = this.filterForm.get(this.formControlDateStartName)?.value;
    const dateEnd = this.filterForm.get(this.formControlDateEndName)?.value;

    if (!dateStart && !dateEnd) {
      this.filterForm.get(this.formControlDateStartName)?.setValue(date);
    } else if (dateStart && !dateEnd && date && date.after(dateStart)) {
      this.filterForm.get(this.formControlDateEndName)?.setValue(date);
      this.datePicker.close();
    } else {
      this.filterForm.get(this.formControlDateEndName)?.setValue(null);
      this.filterForm.get(this.formControlDateStartName)?.setValue(date);
    }

    if (this.formControlDateQuickRangeKeyName) {
      this.filterForm.get(this.formControlDateQuickRangeKeyName)?.setValue(null);
    }
  }

  onClearDate() {
    this.filterForm.get(this.formControlDateStartName)?.setValue(null);
    this.filterForm.get(this.formControlDateEndName)?.setValue(null);
    if (this.formControlDateQuickRangeKeyName) {
      this.filterForm.get(this.formControlDateQuickRangeKeyName)?.setValue(null);
    }
    this.hoveredDate = null;
  }

  isHovered(date: NgbDate) {
    const dateStart = this.filterForm.get(this.formControlDateStartName)?.value;
    const dateEnd = this.filterForm.get(this.formControlDateEndName)?.value;
    return dateStart && !dateEnd && this.hoveredDate && date.after(dateStart) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    const dateStart = this.filterForm.get(this.formControlDateStartName)?.value;
    const dateEnd = this.filterForm.get(this.formControlDateEndName)?.value;
    return dateEnd && date.after(dateStart) && date.before(dateEnd);
  }

  isStart(date: NgbDate) {
    const dateStart = this.filterForm.get(this.formControlDateStartName)?.value;
    return date.equals(dateStart);
  }

  isEnd(date: NgbDate) {
    const dateEnd = this.filterForm.get(this.formControlDateEndName)?.value;
    return date.equals(dateEnd);
  }

  isRange(date: NgbDate) {
    const dateStart = this.filterForm.get(this.formControlDateStartName)?.value;
    const dateEnd = this.filterForm.get(this.formControlDateEndName)?.value;
    return date.equals(dateStart) || (dateEnd && date.equals(dateEnd)) || this.isInside(date) || this.isHovered(date);
  }

  get dateRangeLabel(): string {
    const key: QuickRangeKey | null = this.formControlDateQuickRangeKeyName
      ? this.filterForm.get(this.formControlDateQuickRangeKeyName)?.value
      : null;

    if (key) {
      return this.quickRanges.get(key)?.label ?? '';
    }

    const start: NgbDate | null = this.filterForm.get(this.formControlDateStartName)?.value;
    const end: NgbDate | null = this.filterForm.get(this.formControlDateEndName)?.value;

    if (!start || !end) {
      return '';
    }

    const format = (d: NgbDate) => `${d.day}. ${d.month}. ${d.year}`;
    return `${format(start)} – ${format(end)}`;
  }

  get isSelected(): boolean {
    return !!(
      (this.formControlDateQuickRangeKeyName && this.filterForm.get(this.formControlDateQuickRangeKeyName)?.value) ||
      this.filterForm.get(this.formControlDateStartName)?.value ||
      this.filterForm.get(this.formControlDateEndName)?.value
    );
  }
}
